import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Iter 31.215 — invisible content slot rendered into known points on each
 * page. Fetches the currently-live easter-egg text for {slot} and renders
 * a small, styled-by-parent element. If nothing is live, renders nothing
 * at all — so the surrounding layout stays intact.
 *
 * Six's directive (04 Jun 2026): agents (Two, Three, Sixty-Six) curate the
 * subtle cultural surface autonomously. The frontend just gives them places
 * to write into.
 *
 * Usage:
 *   <EasterEggSlot path="login" slot="login.footer_tagline"
 *     className="text-[10px] tracking-[0.18em] text-ink-soft italic"
 *     fallback={null} />
 */
export default function EasterEggSlot({
    path,
    slot,
    className = "",
    fallback = null,
    as: As = "span",
    showAttribution = false,
}) {
    const [egg, setEgg] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        // Public endpoint — no auth required. Path is included for future
        // per-tenant scoping; backend currently keys by slot only.
        const safePath = (path || "root").replace(/^\//, "").replace(/\//g, "_") || "root";
        api.get(`/easter-eggs/${encodeURIComponent(safePath)}/${encodeURIComponent(slot)}`)
            .then(({ data }) => {
                if (!alive) return;
                if (data && data.live && data.text) setEgg(data);
                setLoaded(true);
            })
            .catch(() => {
                if (!alive) return;
                setLoaded(true);
            });
        return () => { alive = false; };
    }, [path, slot]);

    if (!loaded) return null;
    if (!egg) return fallback;
    return (
        <As
            className={className}
            data-testid={`easter-egg-${slot.replace(/\./g, "-")}`}
            data-egg-source={egg.source_agent_key || "unknown"}
            title={showAttribution ? `Filed by Agent ${egg.source_agent_key || "?"}` : undefined}
        >
            {egg.text}
            {showAttribution && egg.source_agent_key && (
                <span className="opacity-50 ml-1">— {egg.source_agent_key}</span>
            )}
        </As>
    );
}
